import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { useCurrentUser, useDevLogin } from "../lib/currentUser";

/**
 * Search shape: `returnTo` is the URL the user was aiming for when the
 * auth guard kicked them here. Optional — direct visits to /login work
 * the same, just land on `/` after sign-in. `error` is set by the
 * Google OAuth callback when the user cancels the dance.
 */
type LoginSearch = {
  returnTo?: string | undefined;
  error?: string | undefined;
};

export const Route = createFileRoute("/login")({
  validateSearch: (raw: Record<string, unknown>): LoginSearch => {
    const returnTo =
      typeof raw.returnTo === "string" && raw.returnTo.startsWith("/")
        ? raw.returnTo
        : undefined;
    const error = typeof raw.error === "string" ? raw.error : undefined;
    return { returnTo, error };
  },
  component: LoginPage,
});

// Where to point the browser for the Google OAuth handshake.
// Resolved relative to the API base so dev (vite proxy on /api) and
// prod (api.lingua.cardona-apps.com) both work. Full-page navigation —
// not an XHR — because the OAuth dance redirects through Google and
// back via the browser, not the SPA.
const GOOGLE_BASE = `${
  import.meta.env.VITE_API_BASE_URL || "/api"
}/auth/google/login`;

function googleLoginUrl(returnTo?: string): string {
  if (!returnTo) return GOOGLE_BASE;
  // Stash the returnTo in OAuth state so the backend callback can
  // include it in the post-login redirect. The backend has to opt
  // into reading this (S8 in docs/auth-plan.md); until then the
  // returnTo only flows through the dev-stub path.
  return `${GOOGLE_BASE}?returnTo=${encodeURIComponent(returnTo)}`;
}

/**
 * Login surface.
 *
 * Primary: "Sign in with Google" — full-page navigation to
 * /auth/google/login on the API. Always rendered; the backend
 * returns 503 if the OAuth credentials haven't been wired yet, which
 * is the operator's signal to finish the console + Railway env step.
 *
 * Secondary: the dev-stub `email + name` form posts to
 * /auth/dev-login. Disabled in prod by setting AUTH_PROVIDER=google
 * — leaving it visible is intentional so CI / scripts can keep
 * minting sessions without going through Google.
 *
 * The page is deliberately reachable but NOT required during the
 * pre-OAuth transition: the backend's Jorge-fallback in
 * get_current_user keeps the live site working for an anonymous
 * visitor. That fallback comes off once we flip AUTH_PROVIDER=google.
 */
function LoginPage() {
  const navigate = useNavigate();
  const { returnTo, error: oauthError } = Route.useSearch();
  const { data: existing } = useCurrentUser();
  const loginM = useDevLogin();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !name.trim()) return;
    try {
      await loginM.mutateAsync({ email: email.trim(), name: name.trim() });
      // Honour the returnTo from the auth guard. Falls back to the home
      // dashboard for direct /login visits. The `validateSearch` above
      // already pinned returnTo to same-origin paths starting with `/`,
      // so we don't have to re-validate here against open-redirect.
      navigate({ to: returnTo ?? "/" });
    } catch {
      // useMutation surfaces the error on the form via loginM.isError.
    }
  };

  return (
    <section className="mx-auto max-w-md px-4 sm:px-6 py-16 sm:py-24">
      <div className="eyebrow mb-3">Sign in</div>
      <h1 className="font-serif text-3xl sm:text-4xl text-ink-900 mb-3">
        Welcome to lingua
      </h1>
      <p className="text-ink-500 mb-8 leading-relaxed">
        Per-learner progress, separated by language. Sign in to keep your German
        visa-track state across devices. The dev-stub form below is a Phase-1G
        placeholder — the real Google flow lights up when OAuth credentials are
        configured.
      </p>

      {existing && existing.email !== "jorge@cardona-apps.com" && (
        <p className="mb-6 text-sm border-l-2 border-ink-700 bg-ink-50 text-ink-700 px-3 py-2">
          You're already signed in as{" "}
          <span className="font-medium text-ink-900">{existing.name}</span>.
        </p>
      )}

      {oauthError === "oauth_denied" && (
        <p className="mb-6 text-sm border-l-2 border-rose-500 bg-rose-50 text-rose-700 px-3 py-2">
          Google sign-in was cancelled. You can try again or use the dev-stub
          form below.
        </p>
      )}

      {/* Full-page navigation: the OAuth dance leaves the SPA, hits
          Google, comes back through /auth/google/callback. Plain
          anchor so middle-click / keyboard nav work like any link. */}
      <a
        href={googleLoginUrl(returnTo)}
        className="w-full mb-6 border border-ink-900 bg-paper px-4 py-3 text-ink-900 hover:bg-ink-50 transition-colors text-left flex items-center justify-between"
      >
        <span>Sign in with Google</span>
        <span aria-hidden className="text-ink-400">
          →
        </span>
      </a>

      {/* Section divider for the dev-stub form — a centered ornament
          flanked by hairlines reads as "or" without spelling it out. */}
      <div className="mb-4 flex items-center gap-3 text-ink-300">
        <span className="flex-1 hairline" />
        <span className="eyebrow text-ink-400">dev-stub</span>
        <span className="flex-1 hairline" />
      </div>

      <form onSubmit={submit} className="space-y-5">
        {/* Inputs follow §6: no full border, no rounded. 1px underline
            in ink-100, darkens to ink-900 on focus — "fountain-pen on
            paper" instead of "button being pressed". */}
        <label className="block">
          <span className="eyebrow">Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
            className="mt-1 w-full border-0 border-b border-ink-200 bg-transparent px-0 py-2 text-ink-900 placeholder-ink-300 focus:border-ink-900 focus:outline-none transition-colors"
            placeholder="you@example.com"
          />
        </label>
        <label className="block">
          <span className="eyebrow">Display name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="name"
            required
            className="mt-1 w-full border-0 border-b border-ink-200 bg-transparent px-0 py-2 text-ink-900 placeholder-ink-300 focus:border-ink-900 focus:outline-none transition-colors"
            placeholder="Your name"
          />
        </label>
        {loginM.isError && (
          <p className="text-sm text-rose-600">
            Couldn't sign you in. The dev-stub may be disabled in this
            environment, or your email isn't on the allowlist.
          </p>
        )}
        <button
          type="submit"
          disabled={loginM.isPending || !email.trim() || !name.trim()}
          className="w-full bg-accent px-5 py-3 text-accent-fg hover:bg-accent-hover transition-colors disabled:opacity-50"
        >
          {loginM.isPending ? "Signing in…" : "Sign in (dev)"}
        </button>
      </form>
    </section>
  );
}
